import type { OnerwayCodeForm } from 'pixai-contract/payment/shared'
import { match } from 'ts-pattern'
import { Currency, type Interval } from 'src/services/apollo/generated/graphql'
import { executeIntentToPay } from 'src/services/apollo/payment'
import type {
  PaymentExecuteFn,
  PaymentExecuteInput,
  PaymentExecuteResultGmo,
  PaymentExecuteResultOnerway,
  PaymentExecuteResultPaypay,
} from './payment'
import { PaymentProvider } from './payment'
import {
  isOnerwayCardPaymentProvider,
  toWebPaymentBackendProvider,
  usesOneTimeMembershipItem,
} from './paymentProviderMapping'

interface IntentToPayExecutorOpts {
  itemId: string
  oneTimeMembershipId?: string
  interval?: Interval
  currency?: Currency
  shouldDiscount?: boolean
  redirect?: string
  promotionOption?: { extraPackageBoostCode?: string }
}

type IntentToPayOnerwayResult = {
  onerway?: {
    transactionId: string
    subProductType?: string | null
    redirectUrl?: string | null
    codeForm?: OnerwayCodeForm | null
  } | null
}

export function createIntentToPayExecutor(
  opts: IntentToPayExecutorOpts,
): PaymentExecuteFn {
  return (async (
    input: PaymentExecuteInput,
  ): Promise<
    | PaymentExecuteResultOnerway
    | PaymentExecuteResultPaypay
    | PaymentExecuteResultGmo
  > => {
    const backendProvider = toWebPaymentBackendProvider(input.provider)

    const itemId = usesOneTimeMembershipItem(input.provider)
      ? (opts.oneTimeMembershipId ?? opts.itemId)
      : opts.itemId

    const currency = match(input.provider)
      .with(PaymentProvider.paypay, PaymentProvider.gmo, () => Currency.Jpy)
      .with(
        PaymentProvider.onerway,
        PaymentProvider.onerwayJcbAeUnionPay,
        PaymentProvider.onerwayWechatPay,
        () => opts.currency,
      )
      .exhaustive()

    const paymentMethod = match(input)
      .returnType<string | undefined>()
      .with(
        { provider: PaymentProvider.onerway },
        i => i.providerOptions?.paymentMethod,
      )
      .with(
        { provider: PaymentProvider.onerwayJcbAeUnionPay },
        i => i.providerOptions?.paymentMethod,
      )
      .with(
        { provider: PaymentProvider.onerwayWechatPay },
        i => i.providerOptions.paymentMethod,
      )
      .with(
        { provider: PaymentProvider.gmo },
        i => i.providerOptions.paymentMethod,
      )
      .with({ provider: PaymentProvider.paypay }, () => undefined)
      .exhaustive()

    const res = await executeIntentToPay({
      itemId,
      interval: opts.interval,
      opt: {
        provider: backendProvider,
        paymentMethod,
        currency,
        shouldDiscount: opts.shouldDiscount,
        redirect: opts.redirect,
        promotionOption: opts.promotionOption,
        gmpOpt:
          input.provider === PaymentProvider.gmo
            ? { phoneNumber: input.providerOptions.phoneNumber }
            : undefined,
      },
    })

    const result = res.data?.intentToPay

    if (isOnerwayCardPaymentProvider(input.provider)) {
      const transactionId = result?.onerwayTransactionId
      if (!transactionId) throw new Error('Transaction ID not found')
      const orderId = result?.orderId
      if (!orderId) throw new Error('Order ID not found')
      return {
        orderId,
        transactionId,
        subProductType: result?.onerwaySubProductType ?? undefined,
      }
    }

    if (input.provider === PaymentProvider.onerwayWechatPay) {
      // WeChat Pay reads from the structured `onerway` field rather than
      // the deprecated flat `onerwayTransactionId` — that's where the LPMS
      // response carriers (`redirectUrl` for wechat_h5, `codeForm` for
      // wechat_qr) live. Mirror the ORPC executor's flattening in
      // `usePurchaseExtraPackage`: TEXT entry → qrCode (raw deeplink for
      // self-rendering), IMAGE entry → qrCodeUrl (hosted QR image).
      const onerway = (
        result as (typeof result & IntentToPayOnerwayResult) | undefined
      )?.onerway
      if (!onerway) throw new Error('Onerway result missing for WeChat Pay')
      const orderId = result?.orderId
      if (!orderId) throw new Error('Order ID not found')

      // codeForm is JSONObject over GraphQL — narrow to contract type.
      const codeForm = (onerway.codeForm ?? undefined) as
        | OnerwayCodeForm
        | undefined
      const details = codeForm?.codeDetails ?? []
      const qrCode =
        details.find(
          d => d?.codeValueType === 'QRCODE' && d?.displayType === 'TEXT',
        )?.codeValue ?? undefined
      const qrCodeUrl =
        details.find(
          d => d?.codeValueType === 'QRCODE' && d?.displayType === 'IMAGE',
        )?.codeValue ?? undefined

      return {
        orderId,
        transactionId: onerway.transactionId,
        subProductType: onerway.subProductType ?? undefined,
        redirectUrl: onerway.redirectUrl ?? undefined,
        qrCode,
        qrCodeUrl,
      }
    }

    if (input.provider === PaymentProvider.paypay) {
      const url = result?.url
      if (!url) throw new Error('URL not found')
      const orderId = result?.orderId
      if (!orderId) throw new Error('Order ID not found')
      return {
        orderId,
        url,
        codeId: result?.codeId ?? undefined,
        deepLink: result?.deepLink ?? undefined,
      }
    }

    // gmo
    const orderId = result?.orderId
    if (!orderId) throw new Error('Order ID not found')
    const confNo = result?.gmoCvsConfNo
    if (!confNo) throw new Error('Confirmation number not found')
    const receiptNo = result?.gmoCvsReceiptNo
    if (!receiptNo) throw new Error('Receipt number not found')
    const expiredAt = result?.expiredAt
    if (!expiredAt) throw new Error('Expiration date not found')
    return {
      orderId,
      url: result?.url ?? undefined,
      confNo,
      receiptNo,
      expiredAt,
    }
  }) as PaymentExecuteFn
}
